import { useEffect, useState } from 'react';
import { IconLoader2 } from '@tabler/icons-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';

// "Continue with Google". Opens Google's own popup (token client) and hands the access token to `onToken`; the backend checks it
// with Google (POST /api/auth/google). A popup, not Google's embedded button: it also works where cross-origin iframes are blocked.
// Renders nothing when VITE_GOOGLE_CLIENT_ID is not set. `blocked` = something else must happen first (e.g. accept the terms):
// the click then calls `onBlocked` instead of opening Google.
const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
let gsiPromise = null;
const loadGsi = () => {
    if (window.google?.accounts?.oauth2) return Promise.resolve();
    gsiPromise ||= new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = 'https://accounts.google.com/gsi/client';
        s.async = true;
        s.onload = resolve;
        s.onerror = () => { gsiPromise = null; reject(new Error('Google script failed to load')); };
        document.head.appendChild(s);
    });
    return gsiPromise;
};

function GoogleLogo() {
    return (
        <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z" />
            <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z" />
            <path fill="#FBBC05" d="M10.5 28.7A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.8-4.7l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.9-6.1z" />
            <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2.1 1.4-4.9 2.3-8.4 2.3-6.3 0-11.6-4.1-13.5-9.8l-7.9 6.1C6.5 42.6 14.6 48 24 48z" />
        </svg>
    );
}

export default function GoogleButton({ onToken, onClick, blocked = false, onBlocked, disabled = false, label, className = '' }) {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        if (!CLIENT_ID) return undefined;
        let alive = true;
        loadGsi().then(() => { if (alive) setReady(true); }).catch(() => {});
        return () => { alive = false; };
    }, []);

    if (!CLIENT_ID) return null;

    const click = () => {
        if (blocked) { onBlocked?.(); return; }
        if (!ready || busy || disabled) return;
        onClick?.(); // e.g. tick the terms box on the register page
        setBusy(true);
        try {
            const client = window.google.accounts.oauth2.initTokenClient({
                client_id: CLIENT_ID,
                scope: 'openid email profile',
                callback: async (res) => {
                    try { if (res?.access_token) await onToken(res.access_token); }
                    finally { setBusy(false); }
                },
                error_callback: () => setBusy(false), // popup closed / blocked
            });
            client.requestAccessToken({ prompt: 'select_account' });
        } catch {
            setBusy(false);
        }
    };

    return (
        <button
            type="button"
            onClick={click}
            disabled={disabled || busy || (!ready && !blocked)}
            className={`flex w-full items-center justify-center gap-2.5 rounded-md border border-zinc-300 bg-white px-4 py-2.5 text-sm font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100 dark:hover:bg-zinc-700 ${className}`}
        >
            {busy ? <IconLoader2 size={18} className="animate-spin" /> : <GoogleLogo />}
            {label || t('auth.continueWithGoogle')}
        </button>
    );
}
